import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { useSelector } from "react-redux"
import { usePermissionsList } from "../../../hooks/queries/usePermissions"
import { FaGear, FaCircleExclamation } from "react-icons/fa6"
import { DraggableTask } from "./DraggableTask"
import { DroppableSlot } from "./DroppableSlot"
import { IsolatedTimeIndicator } from "./IsolatedTimeIndicator"
import SearchBar from "../SearchBar"
import StatusFilter from "../StatusFilter"
import PatientNurseStats from "../../DailyCalendarFiles/TopLevelStats"
import CalendarSettings from "../../DailyCalendarFiles/CustomCalendar/CalendarSettings"
import EditPatient from "../EditPatient"
import LoadingSkeleton from "../LoadingSkeleton"
import AddNursePopup from "../../DailyCalendarFiles/CustomCalendar/AddNursePopup/AddNursePopup"
import RemoveNursePopup from "../../DailyCalendarFiles/CustomCalendar/RemoveNursePopup"
import {
	getScheduleSelection,
	getScheduleForSelection
} from "../../DailyCalendarFiles/CustomCalendar/scheduleSelection"

import {
	hasOverlap,
	startTimeAllCases,
	endTimeAllCases,
	hasPermission,
	allowedToEdit
} from "../../../utils/realTimeUtils"
import {
	convertMinutesTo12HHMM,
	convertTo12HourFormat,
	getCurrentTime
} from "../../../utils/generalUtils"
import { getThemeColors, getThemeColorsText } from "../../../utils/themeColors"
import {
	calculateNurseTotalAppointments,
	calculateTotalAcuity
} from "../../../utils/realTimeNurseUtils"
import { doPatientsOverlapOnChair, getChairType } from "../../../utils/realTimeChairUtils"
import { FiEdit } from "react-icons/fi"
import { IoIosSwap } from "react-icons/io"
import EditNursePopup from "../editNurse"
import SwapAssignmentPopup from "../swapAssignmentPopup"

const BaseGantt = ({
	viewType, // "nurse" or "chair"
	setNotification,
	patientSchedule,
	nurseSchedule,
	chairSchedule,
	openTime,
	closeTime,
	documentId,
	resourceId = null,
	resourceName = null,
	isEditing, // From Redux state
	logging // For logging service
}) => {
	// Keep memoized selectors for stable Redux state
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const colorScheme = useSelector(state => state.user.colorScheme)
	const viewMode = useSelector(state => state.user.viewMode)
	const select = useSelector(state => state.user.select)
	const permissions = usePermissionsList()

	// Keep memoization for stable user preferences
	const userPreferences = useMemo(
		() => ({ select, viewMode, colorScheme }),
		[select, viewMode, colorScheme]
	)

	// Keep memoization for schedule selection (stable inputs)
	const scheduleSelection = useMemo(
		() => getScheduleSelection(isTomorrowMode, sandbox),
		[isTomorrowMode, sandbox]
	)

	// Remove memoization for current schedule
	const currentSchedule = getScheduleForSelection(scheduleSelection)

	// Remove memoization for resources
	const resources =
		viewType === "nurse"
			? resourceId !== null
				? nurseSchedule.filter(resource => resource.nurseId === resourceId)
				: [...nurseSchedule].sort((a, b) => a.nurseName.localeCompare(b.nurseName))
			: [...chairSchedule].sort((a, b) => a.chairName.localeCompare(b.chairName))

	// Remove memoization for data status
	const dataStatus =
		Boolean(patientSchedule?.length > 0) &&
		Boolean(viewType === "nurse" ? nurseSchedule?.length > 0 : chairSchedule?.length > 0)

	// State
	const [modalOpen, setModalOpen] = useState(false)
	const [tasks, setTasks] = useState(patientSchedule || [])
	const [editMode] = useState(false)
	const [currentTimePosition, setCurrentTimePosition] = useState(null)
	const [selectedTask, setSelectedTask] = useState(null)
	const [showBottomBar, setShowBottomBar] = useState(false)
	const [selectedStatuses, setSelectedStatuses] = useState([])
	const [, setLastUpdateTime] = useState(new Date())
	const [selectedNurse, setSelectedNurse] = useState(null)
	const [editNursePopupOpen, setEditNursePopupOpen] = useState(false)
	const [swapAssignmentPopupOpen, setSwapAssignmentPopupOpen] = useState(false)
	const [isLoading, setIsLoading] = useState(true)

	// Refs for tracking without causing re-renders
	const isInitialLoadRef = useRef(true)
	const prevViewTypeRef = useRef(viewType)
	const resourceRefs = useRef({})

	// Keep memoization for static calculations
	const totalMinutes = useMemo(() => closeTime - openTime, [closeTime, openTime])
	const colorsBackground = useMemo(
		() => getThemeColors(userPreferences.colorScheme),
		[userPreferences.colorScheme]
	)
	const colorsText = useMemo(
		() => getThemeColorsText(userPreferences.colorScheme),
		[userPreferences.colorScheme]
	)

	// Better loading state management
	useEffect(() => {
		// Check if we need to reset loading state based on view type change
		if (prevViewTypeRef.current !== viewType) {
			setIsLoading(true)
			prevViewTypeRef.current = viewType
			return
		}

		// Only update loading state if we don't have data yet or if it's initial load
		if (isInitialLoadRef.current || isLoading) {
			if (dataStatus) {
				// We have data, transition to loaded state after a brief delay
				const timer = setTimeout(() => {
					setIsLoading(false)
					isInitialLoadRef.current = false
				}, 100)
				return () => clearTimeout(timer)
			}
		}
	}, [dataStatus, viewType, isLoading])

	// Update tasks when patient schedule changes without affecting loading state
	useEffect(() => {
		if (patientSchedule?.length > 0) {
			setTasks(patientSchedule)
		}
	}, [patientSchedule])

	// Time indicator update logic
	const updateTimePosition = useCallback(() => {
		if (isTomorrowMode) {
			setCurrentTimePosition(null)
			return
		}

		const currentMinute = getCurrentTime(sandbox, isTomorrowMode)
		if (currentMinute < openTime || currentMinute > closeTime) {
			setCurrentTimePosition(null)
			return
		}

		const percentageAcrossDay = (currentMinute - openTime) / (closeTime - openTime)
		const newPosition = `calc(16.67% + ${percentageAcrossDay * 83.33}%)`

		setCurrentTimePosition(newPosition)
		setLastUpdateTime(new Date())
	}, [openTime, closeTime, isTomorrowMode, sandbox])

	useEffect(() => {
		updateTimePosition()
		const intervalId = setInterval(updateTimePosition, 10000)
		return () => clearInterval(intervalId)
	}, [updateTimePosition])

	// Remove memoization for move task
	const moveTask = (task, position, targetResourceId) => {
		const { startTime, endTime } = {
			startTime: startTimeAllCases(task, sandbox, isTomorrowMode),
			endTime: endTimeAllCases(task, sandbox, isTomorrowMode)
		}

		setTasks(prevTasks => {
			const newStart = position.x * 10 + openTime
			const newEnd = newStart + (endTime - startTime)

			// Check for overlaps based on viewType
			const hasOverlapInstance =
				viewType === "chair" &&
				tasks.some(
					t =>
						t.patientId !== task.patientId &&
						t.assignedChair === targetResourceId &&
						hasOverlap(
							{ scheduledStartTime: newStart, scheduledEndTime: newEnd },
							t,
							sandbox,
							isTomorrowMode
						)
				)

			if (hasOverlapInstance) {
				alert("Cannot move task to this slot due to overlap.")
				return prevTasks
			}

			const isValid = newStart >= openTime && newEnd <= closeTime

			if (isValid) {
				return prevTasks.map(t =>
					t.patientId === task.patientId
						? {
								...t,
								scheduledStartTime: newStart,
								scheduledEndTime: newEnd,
								[viewType === "nurse" ? "assignedNurse" : "assignedChair"]:
									targetResourceId
							}
						: t
				)
			}
			return prevTasks
		})
	}

	// Keep memoization for search (stable input)
	const handleSearchResultClick = useCallback(
		result => {
			if (
				viewType === "nurse" &&
				result.type === "nurse" &&
				resourceRefs.current[result.id]
			) {
				const element = resourceRefs.current[result.id]
				const elementRect = element.getBoundingClientRect()
				const absoluteElementTop = elementRect.top + window.pageYOffset
				const middle = absoluteElementTop - window.innerHeight / 4
				window.scrollTo({ top: middle, behavior: "smooth" })
			} else if (viewType === "chair" && result.type === "chair") {
				const chairElement = document.querySelector(`[data-chair-id="${result.id}"]`)
				if (chairElement) {
					const elementRect = chairElement.getBoundingClientRect()
					const absoluteElementTop = elementRect.top + window.pageYOffset
					const middle = absoluteElementTop - window.innerHeight / 4
					window.scrollTo({ top: middle, behavior: "smooth" })
				}
			} else if (result.type === "patient") {
				const patient = result.originalItem
				const resourceId =
					viewType === "nurse" ? patient.assignedNurse : patient.assignedChair
				const element =
					viewType === "nurse"
						? resourceRefs.current[resourceId]
						: document.querySelector(`[data-chair-id="${resourceId}"]`)

				if (element) {
					const elementRect = element.getBoundingClientRect()
					const absoluteElementTop = elementRect.top + window.pageYOffset
					const middle = absoluteElementTop - window.innerHeight / 4
					window.scrollTo({ top: middle, behavior: "smooth" })
					setSelectedTask(patient)
				}
			}
		},
		[viewType]
	)

	// Remove memoization for render tasks
	const renderGanttTasks = () => {
		return resources?.map((resource, resourceIndex) => {
			const lines = tasks
				.filter(task => {
					const resourceMatch =
						viewType === "nurse"
							? task.assignedNurse === resource.nurseId
							: task.assignedChair === resource.chairId
					return (
						resourceMatch &&
						(selectedStatuses.length === 0 ||
							selectedStatuses.some(selected =>
								selected.value.some(val => task.internalStatus.includes(val))
							))
					)
				})
				.reduce((acc, task) => {
					const taskExists = acc.some(line =>
						line.some(t => t.patientId === task.patientId)
					)
					if (taskExists) {
						return acc
					}

					const preferredRowIndex = task.row

					while (acc.length <= preferredRowIndex) {
						acc.push([])
					}

					const preferredRow = acc[preferredRowIndex]

					if (
						preferredRow &&
						!preferredRow.some(t => hasOverlap(t, task, sandbox, isTomorrowMode))
					) {
						acc[preferredRowIndex].push(task)
					} else {
						const lineFound = acc.find(
							(line, index) =>
								index !== preferredRowIndex &&
								!line.some(t => hasOverlap(t, task, sandbox, isTomorrowMode))
						)

						if (lineFound) {
							const rowIndex = acc.indexOf(lineFound)
							lineFound.push({ ...task, row: rowIndex })
						} else {
							acc.push([{ ...task, row: acc.length }])
						}
					}

					return acc
				}, [])

			// Ensure minimum of 3 rows for nurse view, 1 row for chair view
			const minRows = viewType === "nurse" ? 3 : 1
			while (lines.length < minRows) {
				lines.push([])
			}
			if (editMode) lines.push([])

			const isDetailedView = userPreferences.viewMode === "Detailed"
			const propertyCount = isDetailedView || userPreferences.viewMode !== "Minimal" ? 2 : 1
			const baseHeightPerProperty = 30
			const padding = 10
			const heightPerTask = propertyCount * baseHeightPerProperty + padding
			const calculatedHeight = `${lines.length * heightPerTask}px`

			const patientsOverlapOnChair =
				viewType === "chair" &&
				doPatientsOverlapOnChair(resource, tasks, sandbox, isTomorrowMode)

			return (
				<React.Fragment key={viewType === "nurse" ? resource.nurseId : resource.chairId}>
					{resourceIndex > 0 && <div className="h-px bg-gray-300 w-full my-2" />}
					<div
						className={`flex border ${
							patientsOverlapOnChair ? "border-2 border-red-500" : "border-none"
						}`}
						ref={el =>
							viewType === "nurse" && (resourceRefs.current[resource.nurseId] = el)
						}
						data-chair-id={viewType === "chair" ? resource.chairId : undefined}
					>
						{/* Resource header */}
						<div
							className={`flex-none w-1/6 p-4 font-bold text-sm resourceStart flex flex-col border-r border-gray-300 ${
								patientsOverlapOnChair ? "bg-red-100" : ""
							} ${resourceIndex === 0 ? (viewType === "nurse" ? "firstNurse" : "firstChair") : ""}`}
						>
							<div className="flex flex-col items-start justify-start h-full">
								<div className="flex flex-wrap items-center gap-2 w-full text-left break-words overflow-wrap break-word">
									{viewType === "nurse" ? resource.nurseName : resource.chairName}
									{viewType === "nurse" &&
										(hasPermission(permissions, "write:schedules") ||
											sandbox) && (
											<button
												className="p-1.5 hover:bg-gray-100 hover:text-customBlue transition-colors duration-200 rounded-full flex items-center justify-center"
												onClick={() => {
													if (!isEditing) {
														setSelectedNurse(resource)
														setEditNursePopupOpen(true)
													}
												}}
												title="Edit Nurse Details"
											>
												<FiEdit className="h-3.5 w-3.5" />
											</button>
										)}
									{patientsOverlapOnChair && (
										<div className="relative group inline-flex items-center">
											<FaCircleExclamation
												aria-hidden="true"
												className="h-5 w-5 text-red-500"
											/>
											<div
												className="absolute left-0 top-full mt-2 bg-black text-white text-base rounded-md hidden group-hover:block z-10"
												style={{ width: "200px", padding: "8px" }}
											>
												Patients overlap on this chair
											</div>
										</div>
									)}
								</div>
								{renderResourceHeader(resource)}
								{viewType === "nurse" &&
									(hasPermission(permissions, "write:schedules") || sandbox) && (
										<button
											className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none mt-2"
											onClick={() => {
												setSelectedNurse(resource)
												setSwapAssignmentPopupOpen(true)
											}}
										>
											Swap Patients{" "}
											<IoIosSwap className="h-4 w-4 text-gray-600" />
										</button>
									)}
							</div>
						</div>

						{/* Tasks area */}
						<div
							className="relative flex-grow cursor-pointer taskStart"
							style={{ height: calculatedHeight }}
						>
							{/* Background for resource schedule */}
							<div
								className={`absolute inset-y-0 ${
									patientsOverlapOnChair ? "bg-red-100" : "bg-blue-100 opacity-50"
								}`}
								style={{
									left:
										viewType === "nurse"
											? `${((Math.max(resource.scheduledStartTime, openTime) - openTime) * 100) / totalMinutes}%`
											: "0",
									width:
										viewType === "nurse"
											? `${((Math.min(resource.scheduledEndTime, closeTime) - Math.max(resource.scheduledStartTime, openTime)) * 100) / totalMinutes}%`
											: "100%"
								}}
							/>

							{/* Lunch break shading for nurses */}
							{viewType === "nurse" &&
								resource.scheduledLunchBreakStart !== null &&
								resource.scheduledLunchBreakEnd !== null && (
									<div
										className="absolute inset-y-0 bg-blue-cross-hatched"
										style={{
											left: `${((resource.scheduledLunchBreakStart - openTime) * 100) / totalMinutes}%`,
											width: `${((resource.scheduledLunchBreakEnd - resource.scheduledLunchBreakStart) * 100) / totalMinutes}%`,
											top: 0,
											bottom: 0,
											zIndex: 0
										}}
									/>
								)}

							{/* Droppable slots */}
							<div className="absolute inset-0">
								{lines.map((line, lineIndex) =>
									Array.from({ length: totalMinutes / 10 }).map((_, index) => (
										<DroppableSlot
											key={`${lineIndex}-${index}`}
											index={index}
											resourceId={
												viewType === "nurse"
													? resource.nurseId
													: resource.chairId
											}
											moveTask={moveTask}
											editMode={editMode}
											topOffset={lineIndex * heightPerTask}
											view={userPreferences.viewMode}
											sandbox={sandbox}
											isTomorrowMode={isTomorrowMode}
											totalMinutes={totalMinutes}
											openTime={openTime}
										/>
									))
								)}
							</div>

							{/* Tasks */}
							{lines?.map((line, i) => (
								<div key={i} className="relative flex items-center">
									{line?.map(task => (
										<DraggableTask
											key={task.patientId}
											task={task}
											editMode={editMode}
											onClick={() => handleTaskClick(task)}
											viewType={viewType}
											color={userPreferences.select}
											colorsBackground={colorsBackground}
											colorsText={colorsText}
											view={userPreferences.viewMode}
											heightPerTask={heightPerTask}
											sandbox={sandbox}
											isTomorrowMode={isTomorrowMode}
											resourceId={
												viewType === "nurse"
													? resource.nurseId
													: resource.chairId
											}
											nurseSchedule={nurseSchedule}
											chairSchedule={chairSchedule}
											currentSchedule={currentSchedule}
										/>
									))}
								</div>
							))}

							{/* Time grid lines */}
							<div className="absolute inset-0 pointer-events-none">
								{/* Hour lines */}
								{Array.from(
									{ length: Math.ceil(totalMinutes / 60) },
									(_, i) => Math.floor(openTime / 60) + i
								).map(
									hour =>
										hour * 60 >= openTime && (
											<div
												key={hour}
												className="absolute border-l border-gray-300"
												style={{
													left: `${((hour * 60 - openTime) * 100) / totalMinutes}%`,
													top: 0,
													bottom: 0
												}}
											/>
										)
								)}

								{/* Final vertical line */}
								<div
									className="absolute border-l border-gray-300"
									style={{
										left: `${(((Math.ceil(totalMinutes / 60) + Math.floor(openTime / 60)) * 60 - openTime) * 100) / totalMinutes}%`,
										top: 0,
										bottom: 0
									}}
								/>
							</div>
						</div>
					</div>
				</React.Fragment>
			)
		})
	}

	// Render methods
	const renderResourceHeader = resource => {
		if (viewType === "nurse") {
			return (
				<div className="flex flex-col space-y-1 w-full">
					<div className="text-gray-400 w-full text-left">
						Appointments:{" "}
						{calculateNurseTotalAppointments(resource, patientSchedule) || 0}
					</div>
					<div className="text-gray-400 w-full text-left">
						Acuity: {calculateTotalAcuity(resource, patientSchedule) || 0}
					</div>
					<div className="text-gray-400 w-full text-left">
						Shift: {convertMinutesTo12HHMM(resource.scheduledStartTime)} -{" "}
						{convertMinutesTo12HHMM(resource.scheduledEndTime)}
					</div>
				</div>
			)
		} else {
			return (
				<div className="text-gray-400 w-full text-left">Type: {getChairType(resource)}</div>
			)
		}
	}

	// New handleTaskClick function
	const handleTaskClick = task => {
		if (editMode) {
			// pass
		} else if (
			!sandbox &&
			(hasPermission(permissions, "write:schedules") ||
				hasPermission(permissions, "write:chairs") ||
				hasPermission(permissions, "read:schedule-personal")) &&
			!allowedToEdit(task, sandbox, isTomorrowMode)
		) {
			setNotification({
				visible: true,
				message: "You cannot edit this visit because it has ended.",
				success: false
			})
			setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
		} else {
			setSelectedTask(task)
		}
	}

	// New scroll handler for bottom bar
	useEffect(() => {
		const handleScroll = () => {
			const firstElement = document.querySelector(
				viewType === "nurse" ? ".firstNurse" : ".firstChair"
			)
			if (firstElement) {
				setShowBottomBar(
					window.scrollY >= firstElement.offsetTop + firstElement.offsetHeight
				)
			}
		}

		window.addEventListener("scroll", handleScroll)
		return () => window.removeEventListener("scroll", handleScroll)
	}, [viewType])

	// Simplified loading check
	if (isLoading) {
		return <LoadingSkeleton />
	}

	return (
		<>
			<CalendarSettings modalOpen={modalOpen} setModalOpen={setModalOpen} />
			<div className="my-4 z-50">
				<PatientNurseStats
					patientSchedule={patientSchedule}
					nurseSchedule={nurseSchedule}
				/>
			</div>
			{/* Search and filters */}
			<div className="flex items-center justify-between mb-6 z-50">
				<SearchBar
					searchableItems={[
						{
							items: viewType === "nurse" ? nurseSchedule : chairSchedule,
							type: viewType,
							getSearchableText: item =>
								viewType === "nurse" ? item.nurseName : item.chairName,
							getId: item => (viewType === "nurse" ? item.nurseId : item.chairId),
							getName: item =>
								viewType === "nurse" ? item.nurseName : item.chairName,
							getDisplayText: item =>
								`${viewType === "nurse" ? "Nurse" : "Chair"}: ${viewType === "nurse" ? item.nurseName : item.chairName}`,
							iconBgColor: viewType === "nurse" ? "bg-blue-100" : "bg-purple-100",
							iconTextColor:
								viewType === "nurse" ? "text-blue-600" : "text-purple-600"
						},
						{
							items: patientSchedule,
							type: "patient",
							getSearchableText: patient =>
								`${patient.patientName} ${patient.infusionType || ""}`,
							getId: patient => patient.patientId,
							getName: patient => patient.patientName,
							getDisplayText: patient => patient.infusionType || "No type",
							iconBgColor: "bg-green-100",
							iconTextColor: "text-green-600"
						}
					]}
					placeholder={`Search ${viewType}s or patients...`}
					onResultClick={handleSearchResultClick}
				/>

				<div className="flex items-center space-x-4">
					<StatusFilter
						selectedStatuses={selectedStatuses}
						onChange={setSelectedStatuses}
					/>
					<button
						onClick={() => setModalOpen(true)}
						className="bg-customBlue text-white px-4 py-2 rounded-md hover:bg-custom-blue flex items-center"
					>
						<FaGear className="mr-2" />
						Edit Design
					</button>
				</div>
			</div>

			<DndProvider backend={HTML5Backend}>
				<div className="mx-auto bg-white rounded-lg relative">
					{/* Add sticky header with time bar */}
					<div className="sticky top-0 z-40 flex bg-headerBg rounded-t-lg items-center min-h-[70px]">
						{/* Left column for resource names */}
						<div className="flex-none w-1/6 p-4" />

						{/* Time indicators */}
						<div className="flex-grow flex relative">
							{Array.from(
								{ length: Math.ceil(totalMinutes / 60) },
								(_, i) => Math.floor(openTime / 60) + i
							).map(
								hour =>
									hour * 60 >= openTime && (
										<div
											key={hour}
											className="absolute text-center py-4 text-sm font-semibold text-headerText uppercase tracking-wider"
											style={{
												left: `${((hour * 60 - openTime) * 100) / totalMinutes}%`,
												top: "50%",
												transform: "translateY(-50%)"
											}}
										>
											{convertTo12HourFormat(hour)}
										</div>
									)
							)}
						</div>
					</div>

					{/* Main content area */}
					<div className="bg-white rounded-b-lg">{renderGanttTasks()}</div>

					{/* Time indicator */}
					{!isTomorrowMode && <IsolatedTimeIndicator position={currentTimePosition} />}
				</div>

				{/* Bottom bar */}
				{showBottomBar && (
					<div className="sticky bottom-0 left-0 right-0 z-[100] bg-white p-4 flex justify-between items-center">
						{/* Color Label and Key */}
						<div className="flex items-center">
							<div className="font-bold text-sm mr-2">{userPreferences.select}:</div>

							<div className="flex flex-wrap items-center">
								{colorsBackground.map((color, index) => (
									<div
										key={index}
										className="flex items-center m-2 ms-3 me-3 font-bold text-sm"
									>
										<div className={`mr-2 w-7 h-7 ${color} rounded-full`}></div>
										{index}
									</div>
								))}
							</div>
						</div>

						{/* Additional Action Buttons */}
						{(hasPermission(permissions, "write:schedules") || sandbox) && (
							<div className="flex flex-wrap items-center space-x-2">
								<AddNursePopup
									setNotification={setNotification}
									isTomorrowMode={isTomorrowMode}
									sandbox={sandbox}
								/>
								<RemoveNursePopup
									setNotification={setNotification}
									isTomorrowMode={isTomorrowMode}
									sandbox={sandbox}
								/>
							</div>
						)}
					</div>
				)}

				{/* Edit patient modal */}
				{selectedTask && (
					<EditPatient
						patient={selectedTask}
						isOpen={
							(hasPermission(permissions, "write:schedules") ||
								hasPermission(permissions, "write:chairs") ||
								hasPermission(permissions, "read:schedule-personal") ||
								sandbox) &&
							allowedToEdit(selectedTask, sandbox, isTomorrowMode)
						}
						onClose={() => setSelectedTask(null)}
						schedule={currentSchedule}
						scheduleSelection={scheduleSelection}
						permissions={permissions}
						setNotification={setNotification}
					/>
				)}

				{viewType === "nurse" &&
					selectedNurse &&
					(hasPermission(permissions, "write:schedules") || sandbox) && (
						<EditNursePopup
							nurse={selectedNurse}
							isOpen={editNursePopupOpen}
							onClose={() => {
								setEditNursePopupOpen(false)
							}}
							patientSchedule={patientSchedule}
							chairSchedule={chairSchedule}
							nurses={nurseSchedule}
							isTomorrowMode={isTomorrowMode}
							setNotification={setNotification}
						/>
					)}
				{viewType === "nurse" &&
					(hasPermission(permissions, "write:schedules") || sandbox) && (
						<SwapAssignmentPopup
							selectedNurse={selectedNurse}
							isOpen={swapAssignmentPopupOpen}
							onClose={() => {
								setSwapAssignmentPopupOpen(false)
							}}
							setNotification={setNotification}
							isTomorrowMode={isTomorrowMode}
							patientSchedule={patientSchedule}
							nurses={nurseSchedule}
						/>
					)}
			</DndProvider>
		</>
	)
}

export default React.memo(BaseGantt)
